import { getPaletteColor } from './color-palette';

/**
 * color-contrast.ts
 *
 * Utilidad para determinar el color de texto legible
 * sobre un color dinámico de la paleta de usuario.
 *
 * Regla: L >= 0.76 en oklch → texto oscuro
 *        L <  0.76 en oklch → texto blanco
 *
 * Aplica en: avatares, headers de estado, badges con fondo sólido.
 */

/** Colores de texto del sistema */
const TEXT_DARK = '#1A2E42'; // foreground del sistema
const TEXT_LIGHT = '#FFFFFF';

/**
 * Extrae el valor de Lightness (L) de un string oklch.
 *
 * Soporta los formatos:
 *   oklch(0.70 0.21 25)
 *   oklch(0.70 0.21 25 / 50%)
 */
function extractOklchLightness(color: string): number | null {
  const match = color.trim().match(/oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)/i);

  if (!match) return null;
  return parseFloat(match[1]);
}

/**
 * Devuelve el color de texto (#FFFFFF o #1A2E42) apropiado
 * para garantizar legibilidad sobre el color de fondo dado.
 *
 * @param backgroundColor - Valor oklch del color de fondo (ej: "oklch(0.70 0.21 25)")
 * @returns Color de texto en hex
 *
 * @example
 * getTextColor("oklch(0.70 0.21 25)")  // → "#FFFFFF"  (rojo medio)
 * getTextColor("oklch(0.80 0.18 95)")  // → "#1A2E42"  (amarillo claro)
 */
export function getTextColor(backgroundColor: string): string {
  const L = extractOklchLightness(backgroundColor);

  // Si no se puede parsear, fallback a texto oscuro (más seguro)
  if (L === null) return TEXT_DARK;

  return L >= 0.76 ? TEXT_DARK : TEXT_LIGHT;
}

/**
 * Versión tipada para uso con la paleta de colores del sistema.
 * Recibe directamente el valor L numérico si ya fue extraído.
 *
 * @param lightness - Valor L de oklch (0–1)
 * @returns Color de texto en hex
 *
 * @example
 * getTextColorFromLightness(0.80) // → "#1A2E42"
 * getTextColorFromLightness(0.68) // → "#FFFFFF"
 */
export function getTextColorFromLightness(lightness: number): string {
  return lightness >= 0.76 ? TEXT_DARK : TEXT_LIGHT;
}

/**
 * Colores de la paleta que requieren texto oscuro (L >= 0.76).
 * Referencia rápida para documentación.
 *
 * --yellow-solar:    L 0.80  → texto oscuro
 * --yellow-sunbeam:  L 0.79  → texto oscuro
 * --yellow-gold:     L 0.78  → texto oscuro
 * --yellow-honey:    L 0.77  → texto oscuro
 * --lime-zest:       L 0.78  → texto oscuro
 * --lime-electric:   L 0.77  → texto oscuro
 * --lime-spark:      L 0.76  → texto oscuro
 *
 * Todos los demás colores de la paleta → texto blanco.
 */

export function getTextColorFromToken(token: string): string {
  const color = getPaletteColor(token);
  if (!color) return '#FFFFFF';
  return getTextColorFromLightness(color.l);
}

export function getColorValue(token: string): string {
  return getPaletteColor(token)?.value ?? '';
}
